import {
  Box,
  Text,
  Flex,
  FlexProps,
  Icon,
  IconButton,
  CloseButton,
  BoxProps,
  Drawer,
  DrawerContent,
  useColorMode,
  useColorModeValue,
  useDisclosure,
  HStack,
  VStack,
  Avatar,
  WrapItem,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Link,
  Button,
  Badge
} from '@chakra-ui/react';

import {
  FiHome,
  FiTrendingUp,
  FiCompass,
  FiStar,
  FiSettings,
  FiMenu,
  FiBell,
  FiChevronDown,
  FiBookOpen,
  FiUsers,
  FiCalendar,
  FiTrello
} from 'react-icons/fi';

import {IoIosHelpBuoy} from 'react-icons/io';

import {SunIcon, MoonIcon} from '@chakra-ui/icons';

import {Link as RouterLink} from 'react-router-dom';
import {useRoutes} from '../../services/route.service';
import {useStorage} from '../../services/pt-storage.service';
import {usePlanning} from '../../services/planning.service';

import {IconType} from 'react-icons';
import {ReactText, useEffect, useState} from 'react';
import {map, pairwise, startWith} from 'rxjs';
import {Logo} from './Logo';

import styled, {CSSProperties} from 'styled-components';
import {PlanningListState} from '../../pages/Planning/types';
import {Navigation, NavigationItem, NavigationItemProps} from '../Navigation';
import {colors} from '@bettermarks/bm-ui-components';
import {UserMenu} from './UserMenu';

interface LinkItemProps {
  name: string;
  icon: IconType;
}

const CounterBadgeAmount = styled.div`
  line-height: 16px;
  text-align: center;
`;

const CounterBadge = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  flex-flow: column;
  overflow: hidden;
  justify-content: center;
  align-items: center;
  font-size: 12px;
  background: ${colors.cBlue700};
  color: ${colors.cWhite};
  /* position: absolute; */
  right: 4px;
  top: 2px;
  visibility: hidden;
`;

interface AppSidebarProps extends BoxProps {
  // menuItems: NavigationItemProps[];
  onClose?: () => void;
}
const SidebarContent = ({onClose, ...rest}: AppSidebarProps) => {
  const RouteService = useRoutes();
  const StorageService = useStorage();
  const PlanningService = usePlanning();
  const [planningCounter, setCounter] = useState<number>(-1);
  const [planningCounterStyle, setPlanningCounterStyle] = useState<{
    badge: CSSProperties;
    amount: CSSProperties;
  }>({badge: {}, amount: {}});

  const initialPlanningState: PlanningListState = {
    total: -1,
    visible: 0,
    notify: -1
  };
  const [contentLink, setContentLink] = useState<string>(
    RouteService.librarySelection(StorageService.getVersion())
  );

  const resetStyle = (to: number) => {
    setPlanningCounterStyle({
      badge: {visibility: (to === 0 && 'hidden') || 'visible'},
      amount: {}
    });
  };

  useEffect(() => {
    let timeOutId: NodeJS.Timeout;
    const subscr = [
      PlanningService.plState$
        .pipe(
          startWith<PlanningListState>(initialPlanningState),
          pairwise<PlanningListState>(),
          map<[PlanningListState, PlanningListState], [number, number]>(
            ([prev, current]) => [prev.notify, current.notify]
          )
        )
        .subscribe(([from, to]) => {
          if (from >= 0 && from !== to) {
            setPlanningCounterStyle({
              badge: {
                animation: 'update 0.7s',
                visibility: 'visible'
              },
              amount: {
                animation: `${
                  (from < to && 'increase') || 'decrease'
                } 0.6s ease 0.1s`
              }
            });
            timeOutId = setTimeout(() => {
              setCounter(to);
              resetStyle(to);
            }, 700);
          } else {
            resetStyle(to);
            setCounter(to);
          }
        }),
      RouteService.contentRoute$().subscribe(setContentLink)
    ];
    return () => {
      subscr.forEach(s => s.unsubscribe());
      timeOutId && clearTimeout(timeOutId);
    };
  }, []);
  const {colorMode, toggleColorMode} = useColorMode();
  const MenuItems: NavigationItemProps[] = [
    {label: 'Übersicht', icon: FiHome, url: '/overview'},
    {label: 'Tafelbilder', icon: FiTrello, url: '/whiteboard'},
    {label: 'Inhalte', icon: FiBookOpen, url: '/library--select--v2'},
    {
      label: 'Plan',
      icon: FiCalendar,
      url: '/planning',
      badge: (
        <>
          <Badge variant='notification' style={planningCounterStyle.badge}>
            <CounterBadgeAmount style={planningCounterStyle.amount}>
              {(planningCounter > 0 && planningCounter - 1) || 0}
              <br />
              {planningCounter >= 0 && planningCounter}
              <br />
              {planningCounter + 1}
            </CounterBadgeAmount>
          </Badge>
        </>
      )
    },
    {label: 'Klassen', icon: FiUsers, url: '/classes'},
    {label: 'Hilfe', icon: IoIosHelpBuoy, url: '/help'}
  ];
  return (
    <Flex
      transition='3s ease'
      flexDir='column'
      bg={useColorModeValue('white', 'whiteAlpha.50')}
      gap={4}
      boxShadow={useColorModeValue(
        '1px 0px 15px #E8E8E8',
        '1px 0px 15px #131212'
      )}
      borderRight='1px'
      borderRightColor={useColorModeValue('gray.200', 'gray.700')}
      w={{base: 'full', md: 'auto'}}
      h='full'
      {...rest}
    >
      <Flex
        alignItems='center'
        py={4}
        px={6}
        paddingLeft='40px'
        paddingTop={6}
        paddingRight='40px'
        justifyContent='space-between'
      >
        <Logo fill={useColorModeValue('#333', '#fff')} />
      </Flex>
      <Flex px='32px' as='nav' gap={3} flex={1} flexDirection='column'>
        <Navigation menuItems={MenuItems} />
      </Flex>

      <UserMenu />

      <Flex p='24px'>
        <Button
          aria-label='Dark Mode wechseln'
          variant='ghost'
          onClick={toggleColorMode}
        >
          {colorMode === 'light' ? <MoonIcon /> : <SunIcon />}
        </Button>
      </Flex>
    </Flex>
  );
};

interface MobileProps extends FlexProps {
  onOpen: () => void;
}
const MobileNav = ({onOpen, ...rest}: MobileProps) => {
  return (
    <Flex
      ml={{base: 0, md: 60}}
      px={{base: 4, md: 4}}
      height='20'
      alignItems='center'
      bg={useColorModeValue('white', 'gray.900')}
      borderBottomWidth='1px'
      borderBottomColor={useColorModeValue('gray.200', 'gray.700')}
      justifyContent={{base: 'space-between', md: 'flex-end'}}
      {...rest}
    >
      <IconButton
        display={{base: 'flex', md: 'none'}}
        onClick={onOpen}
        variant='outline'
        aria-label='open menu'
        icon={<FiMenu />}
      />

      <Text
        display={{base: 'flex', md: 'none'}}
        fontSize='2xl'
        fontFamily='monospace'
        fontWeight='bold'
      >
        Logo
      </Text>

      <HStack spacing={{base: '0', md: '6'}}>
        <IconButton
          size='lg'
          variant='ghost'
          aria-label='open menu'
          icon={<FiBell />}
        />
        <Flex alignItems={'center'}>
          <Menu>
            <MenuButton transition='all 0.3s' _focus={{boxShadow: 'none'}}>
              <HStack>
                <Avatar
                  size={'sm'}
                  src={
                    'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2070&q=80'
                  }
                />

                <Box display={{base: 'none', md: 'flex'}}>
                  <FiChevronDown />
                </Box>
              </HStack>
            </MenuButton>
            <MenuList
              bg={useColorModeValue('white', 'gray.900')}
              borderColor={useColorModeValue('gray.200', 'gray.700')}
            >
              <WrapItem p={3}>
                <Text fontSize='sm'>Maja Mustermann</Text>
              </WrapItem>

              <MenuItem>Profile</MenuItem>
              <MenuItem>Settings</MenuItem>
              <MenuItem>Billing</MenuItem>
              <MenuDivider />
              <WrapItem p={2}>
                <Button colorScheme='blue' flex={1}>
                  Sign out
                </Button>
              </WrapItem>
            </MenuList>
          </Menu>
        </Flex>
      </HStack>
    </Flex>
  );
};

const AppSidebar = () => {
  const {isOpen, onOpen, onClose} = useDisclosure();
  return (
    <Flex as='aside' h='100vh'>
      <SidebarContent onClose={onClose} />

      {/* <Drawer
        autoFocus={false}
        isOpen={isOpen}
        placement='left'
        onClose={onClose}
        returnFocusOnClose={false}
        onOverlayClick={onClose}
        size='full'
      >
        <DrawerContent>
          <SidebarContent menuItems={menuItems} onClose={onClose} />
        </DrawerContent>
      </Drawer> */}
      {/* <MobileNav onOpen={onOpen} /> */}
    </Flex>
  );
};
export default AppSidebar;
